import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  LogOut,
  Settings,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logout } from "@/utils/authUtils";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isPwaMode } from "@/utils/pwaUtils";
import { cancelAllRequests } from "@/utils/requestUtils";
import { logger } from "@/utils/logger";
import { NotificationBell } from "@/components/notifications/NotificationBell";

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  navItems: {
    path: string;
    icon: React.ElementType;
    label: string;
  }[];
  isActive: (path: string) => boolean;
}

export function MobileSidebar({ isOpen, onClose, navItems, isActive }: MobileSidebarProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isPwa = isPwaMode();

  const handleLogout = async () => {
    try {
      toast({
        title: "Logging out...",
        description: "Clearing all session data",
      });

      // Close the sidebar first for better UX
      onClose();

      // Cancel all pending requests to prevent auth errors
      cancelAllRequests();

      // Short delay to allow the toast to display
      setTimeout(async () => {
        await logout();
      }, 100);
    } catch (error) {
      logger.error("Logout error:", error);

      toast({
        title: "Logout failed",
        description: "There was a problem signing you out",
        variant: "destructive",
      });

      // Still try to redirect even if logout fails
      window.location.replace('/');
    }
  };

  return (
    <>
      <nav
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-background shadow-lg transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          paddingTop: isPwa ? 'env(safe-area-inset-top, 0px)' : '0px'
        }}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <img
              src="/lovable-uploads/f60fddfc-2876-455b-baed-f173808a0bbe.png"
              alt="Logo"
              className="h-8 w-8"
            />
            <h1 className="font-semibold text-lg">VasisPro</h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <X size={20} />
            </Button>
          </div>
        </div>

        <div className="flex flex-col p-2 gap-1 overflow-y-auto h-[calc(100%-60px)]">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant={isActive(item.path) ? "secondary" : "ghost"}
              className={cn(
                "justify-start gap-3 h-12",
                isActive(item.path) && "font-medium"
              )}
              onClick={() => {
                navigate(item.path);
                onClose();
              }}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </Button>
          ))}

          {/* Settings Button */}
          <Button
            variant={isActive("/settings") ? "secondary" : "ghost"}
            className="justify-start gap-3 h-12"
            onClick={() => {
              navigate("/settings");
              onClose();
            }}
          >
            <Settings size={18} />
            <span>Settings</span>
          </Button>

          {/* Logout Button - Moved under Settings, with red styling */}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive/90 border-destructive/50 hover:bg-destructive/10"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </Button>
        </div>
      </nav>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
      )}
    </>
  );
}
